import React, { useEffect, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, NavigateNext, Save } from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import { showNotification } from '../store/notificationSlice';
import { useAppDispatch, useAppSelector } from '../store';
import { addPublicHoliday, updatePublicHoliday as updateHolidayInStore, removePublicHoliday } from '../store/publicHolidaysSlice';
import { apiService } from '../services/api';
import type { PublicHoliday } from '../types/leave';

const PublicHolidays: React.FC = () => {
  const dispatch = useAppDispatch();
  const allHolidays = useAppSelector((s) => s.publicHolidays.publicHolidays);
  const [year, setYear] = useState(() => new Date().getFullYear().toString());

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PublicHoliday | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PublicHoliday | null>(null);

  // Filter holidays by selected year
  const holidays = allHolidays.filter((h) => h.date.startsWith(year));

  return (
    <DashboardLayout>
      <Box>
        <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Leave Management</Typography>
          <Typography variant="body2" color="text.primary" fontWeight={600}>Public Holidays</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Public Holidays</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            Add Holiday
          </Button>
        </Box>

        <Box sx={{ mb: 2 }}>
          <TextField
            label="Year"
            type="number"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            size="small"
            sx={{ width: 120 }}
            inputProps={{ min: 2020, max: 2100 }}
          />
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {holidays.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No public holidays found for {year}.
                  </TableCell>
                </TableRow>
              )}
              {holidays.map((h) => (
                <TableRow key={h.date} hover>
                  <TableCell>{h.date}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{h.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{h.description || '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditTarget(h)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(h)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <CreateHolidayDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={(h) => {
            dispatch(addPublicHoliday(h));
            setCreateOpen(false);
            dispatch(showNotification({ message: 'Holiday added', severity: 'success' }));
          }}
        />

        {editTarget && (
          <EditHolidayDialog
            open
            holiday={editTarget}
            onClose={() => setEditTarget(null)}
            onUpdated={(updated) => {
              dispatch(updateHolidayInStore(updated));
              setEditTarget(null);
              dispatch(showNotification({ message: 'Holiday updated', severity: 'success' }));
            }}
          />
        )}

        {deleteTarget && (
          <DeleteHolidayDialog
            open
            holiday={deleteTarget}
            onClose={() => setDeleteTarget(null)}
            onDeleted={(date) => {
              dispatch(removePublicHoliday(date));
              setDeleteTarget(null);
              dispatch(showNotification({ message: 'Holiday deleted', severity: 'success' }));
            }}
          />
        )}
      </Box>
    </DashboardLayout>
  );
};

// ─── Create Dialog ──────────────────────────────────────────────────────────

interface CreateHolidayDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (h: PublicHoliday) => void;
}

const CreateHolidayDialog: React.FC<CreateHolidayDialogProps> = ({ open, onClose, onCreated }) => {
  const dispatch = useAppDispatch();
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setDate('');
      setName('');
      setDescription('');
      setErrors({});
    }
  }, [open]);

  const handleSubmit = async () => {
    const validationErrors: Record<string, string> = {};
    if (!date) validationErrors['date'] = 'Date is required.';
    if (!name.trim()) validationErrors['name'] = 'Name is required.';
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      const res = await apiService.createPublicHoliday({ date, name: name.trim(), description: description.trim() || undefined });
      onCreated(res.data);
    } catch (err: any) {
      if (err?.jsonMessage) {
        setErrors(err.jsonMessage);
      } else {
        dispatch(showNotification({ message: err?.message ?? 'Failed to create holiday', severity: 'error' }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add Public Holiday</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            size="small"
            fullWidth
            error={!!errors['date']}
            helperText={errors['date']}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
            autoFocus
            error={!!errors['name']}
            helperText={errors['name']}
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <Save />}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Edit Dialog ────────────────────────────────────────────────────────────

interface EditHolidayDialogProps {
  open: boolean;
  holiday: PublicHoliday;
  onClose: () => void;
  onUpdated: (h: PublicHoliday) => void;
}

const EditHolidayDialog: React.FC<EditHolidayDialogProps> = ({ open, holiday, onClose, onUpdated }) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState(holiday.name);
  const [description, setDescription] = useState(holiday.description ?? '');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setName(holiday.name);
      setDescription(holiday.description ?? '');
      setErrors({});
    }
  }, [open, holiday]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setErrors({ name: 'Name is required.' });
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      const res = await apiService.updatePublicHoliday(holiday.date, { name: name.trim(), description: description.trim() || undefined });
      onUpdated(res.data);
    } catch (err: any) {
      if (err?.jsonMessage) {
        setErrors(err.jsonMessage);
      } else {
        dispatch(showNotification({ message: err?.message ?? 'Failed to update holiday', severity: 'error' }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit Public Holiday — {holiday.date}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
            autoFocus
            error={!!errors['name']}
            helperText={errors['name']}
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <Save />}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Delete Dialog ──────────────────────────────────────────────────────────

interface DeleteHolidayDialogProps {
  open: boolean;
  holiday: PublicHoliday;
  onClose: () => void;
  onDeleted: (date: string) => void;
}

const DeleteHolidayDialog: React.FC<DeleteHolidayDialogProps> = ({ open, holiday, onClose, onDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      await apiService.deletePublicHoliday(holiday.date);
      onDeleted(holiday.date);
    } catch (err: any) {
      setError(err.message ?? 'Failed to delete holiday');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Public Holiday</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography>
          Delete <strong>{holiday.name}</strong> ({holiday.date})? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button color="error" variant="contained" onClick={handleConfirm} disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <Delete />}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublicHolidays;
